import React, { createContext, useContext, useEffect, useState } from 'react';
import { get } from '../services';
import { setRollbarPayload, reportError } from '../services/rollbar';

const PlatformContext = createContext(null);

export const usePlatform = () => {
    return useContext(PlatformContext);
};

export const PlatformProvider = ({ children }) => {
    const [platform, setPlatform] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Favicon
    const updateFavicon = (href) => {
        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = href;
    };


    const fetchPlatform = async () => {
        try {
            const data = await get('/platform/');
            if (!data.id) {
                throw new Error('Failed to fetch platform info');
            }
            console.log("got platform data", data);
            setPlatform(data);

            // Which tenant the app booted into decides the theme and the nav, so every
            // report needs it to be reproducible.
            setRollbarPayload({platform: {id: data.id, name: data.name}});

            if (data.favicon_url) {
                updateFavicon(data.favicon_url);
            }

            document.title = data.name || 'Plataforma Senhorio';
            // Meta description
            const metaDescription = document.querySelector("meta[name='description']") || document.createElement('meta');
            metaDescription.name = data.name;
            metaDescription.content = data.name || '';
            if (!metaDescription.parentNode) document.head.appendChild(metaDescription);

            // Apple touch icon
            const existingIcon = document.querySelector("link[rel='apple-touch-icon']");
            if (existingIcon) {
                existingIcon.href = platform.apple_touch_icon_url || '/logo.png';
            } else {
                const icon = document.createElement('link');
                icon.rel = 'apple-touch-icon';
                icon.href = data.logo || '/logo.png';
                document.head.appendChild(icon);
            }

            document.documentElement.style.setProperty('--primary-color', data.primary_color);
            document.documentElement.style.setProperty('--primary-lighter-color', mixColors(data.primary_color, '#ffffff', 0.3));
            document.documentElement.style.setProperty('--secondary-color', data.secondary_color);
            document.documentElement.style.setProperty('--secondary-lighter-color', mixColors(data.secondary_color, '#ffffff', 0.3));
            return data;
        } catch (err) {
            // Nothing renders without this call, and the failure is otherwise only
            // parked in state — report it so a broken tenant boot is visible.
            reportError('critical', 'Platform bootstrap failed', err);
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlatform();
    }, []);

    function mixColors(color1, color2, weight = 0.5) {
        const d2h = d => d.toString(16).padStart(2, '0');
        const h2d = h => parseInt(h, 16);

        color1 = color1.startsWith('#') ? color1.slice(1) : color1;
        color2 = color2.startsWith('#') ? color2.slice(1) : color2;

        let result = '#';

        for (let i = 0; i < 3; i++) {
            const c1 = h2d(color1.substr(i * 2, 2));
            const c2 = h2d(color2.substr(i * 2, 2));
            const mixed = Math.round(c1 * weight + c2 * (1 - weight));
            result += d2h(mixed);
        }

        return result;
    }


    return (
        <PlatformContext.Provider value={{ platform, setPlatform, loading, error, refreshPlatform: fetchPlatform }}>
            {children}
        </PlatformContext.Provider>
    );
};
